<template>
  <ToolDetailDrawer
    v-model="drawerVisible"
    :software-name="selectedSoftwareName" />

  <DeploymentDetailDrawer
    v-model="deploymentDrawerVisible"
    :deployment-id="selectedDeploymentId"
    :deployment-name="selectedDeploymentName" />

  <EmptyState
    v-if="!loadingData && !lastUsageBySoftware?.length && !deployments.length"
    :icon="MonitorDown"
    :title="capitalize(i18n.t('browserExtension.header'))"
    :text="i18n.t('browserExtension.browserextensionDescription')"
    :show-docs-btn="false" />

  <div v-else class="flex h-full flex-col">
    <div class="flex shrink-0 justify-between gap-4">
      <SmInput
        v-model="filter.search"
        outline
        design="form"
        size="medium"
        class="tools-search-input max-w-xs"
        :label="i18n.t('browserExtension.searchToolName')"
        :placeholder="i18n.t('browserExtension.searchToolName')">
      </SmInput>
    </div>

    <div class="relative mt-4 flex min-h-0 flex-1 flex-col overflow-hidden">
      <!-- No search results state -->
      <div
        v-if="!loadingData && filteredTools.length === 0 && filter.search"
        class="flex h-full items-center justify-center">
        <EmptyState
          :icon="Search"
          :title="
            i18n.t('browserExtension.noSearchResultsTitle', 'No results found')
          "
          :text="
            i18n.t(
              'browserExtension.noSearchResultsDescription',
              'No tools match your search. Try a different search term.'
            )
          "
          :create-label="i18n.t('clearSearch', 'Clear search')"
          :show-docs-btn="false"
          @create="filter.search = ''" />
      </div>

      <!-- No data state (no search active) -->
      <div
        v-else-if="!loadingData && groupedTools.length === 0"
        class="flex h-full items-center justify-center">
        <EmptyState
          :icon="AppWindow"
          :title="i18n.t('browserExtension.noToolsTitle', 'No tools detected')"
          :text="
            i18n.t(
              'browserExtension.noToolsDescription',
              'No browser extension usage has been recorded yet.'
            )
          "
          :show-docs-btn="false" />
      </div>

      <SmTable
        v-else
        :data="filteredTools"
        :columns="columns"
        class="h-full"
        :loading="loadingData"
        key-field="_id"
        :expandable="true"
        @row-click="handleRowClick">
        <template #name="{ row }">
          <div class="flex items-center gap-3">
            <p class="font-medium text-gray-900 dark:text-white">
              {{ row.software_name }}
            </p>
          </div>
        </template>
        <template #count="{ row }">
          <div class="flex flex-col gap-1.5">
            <p class="flex items-baseline gap-1.5 text-base">
              <span class="font-bold text-gray-900 dark:text-white">{{
                row.count
              }}</span>
              <span class="text-sm text-gray-500 dark:text-gray-400">{{
                i18n.t('browserExtension.sessionsDetected')
              }}</span>
            </p>
          </div>
        </template>
        <template #lastUsage="{ row }">
          <div>
            <p>{{ displayDate(dayjs(row.latestUsage).toDate()) }}</p>
          </div>
        </template>

        <!-- Expanded View: Breakdown by Deployment -->
        <template #expand="{ row }">
          <div
            class="border-t border-gray-100 bg-gray-50 p-6 dark:border-gray-700 dark:bg-gray-800/50">
            <!-- Header with summary -->
            <div class="mb-5 flex items-center justify-between">
              <p
                class="text-sm font-semibold uppercase tracking-wider text-gray-600 dark:text-gray-400">
                {{ i18n.t('browserExtension.deploymentBreakdown') }}
              </p>
            </div>

            <!-- Deployment Cards -->
            <div class="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-3">
              <DeploymentCard
                v-for="detail in row.details"
                :key="detail.deployment_id || detail.deployment_name"
                :deployment="detail"
                @click="handleDeploymentClick" />
            </div>
          </div>
        </template>
      </SmTable>
    </div>
  </div>
</template>
<script lang="ts" setup>
  import { useI18n } from 'vue-i18n'
  import { MonitorDown, AppWindow, Search } from 'lucide-vue-next'
  import dayjs from 'dayjs'
  import { onUnmounted } from 'vue'
  import {
    useBrowserDeployments,
    useBrowserLastUsageBySoftware,
    type BrowserLastUsageBySoftware,
  } from '@/stores/browserextension/browserextension.store'
  import { displayDate } from '@/utils/formatter/date'
  import { BrowserLastUsageBySoftwareFilters } from '@/stores/browserextension/browserextension.filter'
  import { capitalize } from '@/utils/formatter/formatter'
  import { Column } from '../../sm/SmTable.types'
  import DeploymentCard from '../dashboard/widgets/DeploymentCard.vue'
  import EmptyState from '@/components/shared/EmptyState.vue'
  import DeploymentDetailDrawer from '../drawers/DeploymentDetailDrawer.vue'

  const i18n = useI18n()

  const loadingData = ref(true)
  const filter = ref<BrowserLastUsageBySoftwareFilters>({ search: '' })
  const { data: lastUsageBySoftware, unsubscribe: unsubLastUsage } =
    useBrowserLastUsageBySoftware(filter.value)
  const { data: deployments, unsubscribe: unsubDeployments } =
    useBrowserDeployments()

  // Cleanup subscriptions on unmount to prevent memory leaks
  onUnmounted(() => {
    unsubLastUsage()
    unsubDeployments()
  })

  watch(
    () => [lastUsageBySoftware, deployments],
    ([newLastUsageBySoftware, newDeployments]) => {
      if (
        newLastUsageBySoftware !== undefined &&
        newDeployments !== undefined
      ) {
        loadingData.value = false
      }
    },
    { deep: true }
  )

  // ----------------------------------------------------------------------
  // Data Grouping
  // ----------------------------------------------------------------------

  interface GroupedTool {
    _id: string
    software_name: string
    count: number
    latestUsage: number
    details: BrowserLastUsageBySoftware[]
  }

  const groupedTools = computed<GroupedTool[]>(() => {
    if (!lastUsageBySoftware.value) return []

    const groups = new Map<string, GroupedTool>()

    for (const item of lastUsageBySoftware.value) {
      if (!groups.has(item.software_name)) {
        groups.set(item.software_name, {
          _id: item.software_name, // Use name as ID for the parent row
          software_name: item.software_name,
          count: 0,
          latestUsage: 0,
          details: [],
        })
      }

      const group = groups.get(item.software_name)!
      // Handle count which can be string | number | bigint
      const itemCount =
        typeof item.count === 'string'
          ? parseInt(item.count, 10)
          : Number(item.count ?? 0)
      group.count += itemCount

      // Handle latestUsage which can be string timestamp
      if (item.latestUsage) {
        const itemLatest =
          typeof item.latestUsage === 'string'
            ? new Date(item.latestUsage).getTime()
            : 0
        if (itemLatest > group.latestUsage) {
          group.latestUsage = itemLatest
        }
      }
      group.details.push(item)
    }

    // Sort by count descending
    return Array.from(groups.values()).sort((a, b) => b.count - a.count)
  })

  // Filter by search
  const filteredTools = computed<GroupedTool[]>(() => {
    if (!filter.value.search) return groupedTools.value

    const searchLower = filter.value.search.toLowerCase()
    return groupedTools.value.filter((tool) =>
      tool.software_name.toLowerCase().includes(searchLower)
    )
  })

  // ----------------------------------------------------------------------
  // Table
  // ----------------------------------------------------------------------

  const columns = computed<Column<GroupedTool>[]>(() => [
    {
      key: 'name',
      label: capitalize(i18n.t('name')),
      width: 2,
      sortable: true,
      loader: true,
    },
    {
      key: 'count',
      label: capitalize(i18n.t('browserExtension.totalSessions')),
      width: 1,
      sortable: true,
      loader: true,
    },
    {
      key: 'lastUsage',
      label: capitalize(i18n.t('browserExtension.lastDetected')),
      width: 1,
      sortable: true,
      loader: true,
    },
  ])

  // Drawer
  import ToolDetailDrawer from '../drawers/ToolDetailDrawer.vue'

  const drawerVisible = ref(false)
  const selectedSoftwareName = ref<string | null>(null)

  function handleRowClick(index: number, row: GroupedTool) {
    selectedSoftwareName.value = row.software_name
    drawerVisible.value = true
  }

  // Deployment Drawer
  const deploymentDrawerVisible = ref(false)
  const selectedDeploymentId = ref<string | null>(null)
  const selectedDeploymentName = ref<string | undefined>(undefined)

  function handleDeploymentClick(deployment: BrowserLastUsageBySoftware) {
    selectedDeploymentId.value = deployment.deployment_id || null
    selectedDeploymentName.value = deployment.deployment_name
    deploymentDrawerVisible.value = true
  }
</script>

<style scoped lang="scss">
  .tools-search-input :deep(.sm-input) {
    height: 1.5rem;
  }
</style>
